import React, { FC, useCallback, useEffect } from 'react'

import { SearchAutoCompleteQuery } from '../../../../graphql/search'
import useOutsideClick from '../../../../hooks/useOutsideClick'
import Spinner from '../../../Spinner'
import { SearchResult } from '../SearchResult'
import * as Styled from './styled'

import NoSearchResultsIcon from '../../../../img/eye-off.svg'

export interface SearchDropdownProps {
  keywords: string
  shouldShow: boolean
  closeSearch: () => void
  products: {
    show: boolean
    showInvalid: boolean
    loading: boolean
    title: string
    list: SearchAutoCompleteQuery['autoComplete']['results']
  }
  searchTerms: {
    show: boolean
    list: string[]
  }
  categories: {
    show: boolean
    loading: boolean
    list: string[]
  }
}

const termsAndCategoriesMaxDisplay = 12

const HighlightMatch: React.VFC<{
  searchTerm: string
  keywords: string
}> = ({ searchTerm, keywords }) => {
  const matchText = searchTerm.slice(0, keywords.length)
  const restText = searchTerm.slice(keywords.length)

  return (
    <>
      <Styled.HighlightedMatch>{matchText}</Styled.HighlightedMatch>
      {restText}
    </>
  )
}

const InvalidSearch: React.VFC = () => (
  <Styled.NoSearchResultsContainer>
    <Styled.NoSearchResultsIcon src={NoSearchResultsIcon} />
    <div>
      <Styled.NoSearchResultsTitle>No products found</Styled.NoSearchResultsTitle>
      <Styled.NoSearchResultsSubtitle>
        Check these recommended products
      </Styled.NoSearchResultsSubtitle>
    </div>
  </Styled.NoSearchResultsContainer>
)

const ItemsList: React.VFC<{
  list: SearchAutoCompleteQuery['autoComplete']['results']
  disableScroll?: boolean
  closeSearch?: () => void
}> = ({ list, disableScroll, closeSearch }) => {
  return (
    <Styled.SuggestItemList disableScroll={disableScroll}>
      {list.map((product) => (
        <Styled.SuggestItemListEntry key={product.id}>
          <SearchResult result={product} closeSearch={closeSearch} />
        </Styled.SuggestItemListEntry>
      ))}
    </Styled.SuggestItemList>
  )
}

export const SearchDropdown: FC<SearchDropdownProps> = ({
  keywords,
  shouldShow,
  closeSearch,
  products,
  searchTerms,
  categories,
}) => {
  useEffect(() => {
    const closeOnEscape = (event: { key: string }) => {
      if (event.key === 'Escape') {
        closeSearch()
      }
    }
    document.addEventListener('keyup', closeOnEscape)
    return () => {
      document.removeEventListener('keyup', closeOnEscape)
    }
  }, [closeSearch])

  const { elementRef: clickOutsideSearchAutoCompleteRef } = useOutsideClick<HTMLDivElement>(
    useCallback(() => closeSearch(), [closeSearch]),
    {
      shouldIgnore: (event: MouseEvent) => {
        let element = event.target as HTMLElement | null
        while (element) {
          if (element.id === 'search') {
            return true
          }
          element = element.parentElement
        }
        return false
      },
    },
  )

  useEffect(() => {
    if (shouldShow) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [shouldShow])

  return (
    <Styled.SearchContainer role="search" $show={shouldShow}>
      <Styled.AutoCompleteContainer ref={clickOutsideSearchAutoCompleteRef}>
        <Styled.ContentLayout>
          <Styled.SearchTermsAndCategoriesContainer>
            <Styled.SearchTermsContainer
              $showProducts={products.show}
              $showSearchTerms={searchTerms.show}
            >
              {searchTerms.show ? (
                <>
                  <Styled.Title>Search Terms</Styled.Title>
                  <Styled.CategoriesAndSearchTermsList>
                    {searchTerms.list.length > 0 ? (
                      searchTerms.list
                        .slice(0, termsAndCategoriesMaxDisplay - 1)
                        .map((searchTerm, index) => (
                          <Styled.CategoriesAndSearchTermsListItem key={`${index}:${searchTerm}`}>
                            <Styled.Link
                              to={`/search-results?keywords=${encodeURIComponent(searchTerm ?? '')}`}
                              onClick={() => closeSearch()}
                            >
                              <HighlightMatch searchTerm={searchTerm ?? ''} keywords={keywords} />
                            </Styled.Link>
                          </Styled.CategoriesAndSearchTermsListItem>
                        ))
                    ) : (
                      <Styled.CategoriesAndSearchTermsListItem>
                        No suggestions
                      </Styled.CategoriesAndSearchTermsListItem>
                    )}
                  </Styled.CategoriesAndSearchTermsList>
                </>
              ) : null}
            </Styled.SearchTermsContainer>
            {categories.show ? (
              <Styled.CategoriesContainer>
                <Styled.Title>Categories</Styled.Title>
                {categories.loading ? null : (
                  <Styled.CategoriesAndSearchTermsList>
                    {categories.list.length > 0 ? (
                      categories.list.slice(0, termsAndCategoriesMaxDisplay).map(
                        (category, index) =>
                          category && (
                            <Styled.CategoriesAndSearchTermsListItem
                              key={`${index + 1}:${category}`}
                            >
                              <Styled.Link
                                to={`/products?category=${encodeURIComponent(category)}`}
                                onClick={() => closeSearch()}
                              >
                                {category}
                              </Styled.Link>
                            </Styled.CategoriesAndSearchTermsListItem>
                          ),
                      )
                    ) : (
                      <Styled.CategoriesAndSearchTermsListItem>
                        No suggestions
                      </Styled.CategoriesAndSearchTermsListItem>
                    )}
                  </Styled.CategoriesAndSearchTermsList>
                )}
              </Styled.CategoriesContainer>
            ) : null}
          </Styled.SearchTermsAndCategoriesContainer>
          {products.show ? (
            <Styled.ProductsContainer>
              <Styled.Title>{products.title}</Styled.Title>
              {products.loading ? (
                <Spinner loading />
              ) : products.showInvalid ? (
                <Styled.InvalidSearchContainer>
                  <InvalidSearch />
                  <ItemsList list={products.list} closeSearch={closeSearch} disableScroll />
                </Styled.InvalidSearchContainer>
              ) : (
                <ItemsList list={products.list} closeSearch={closeSearch} />
              )}
            </Styled.ProductsContainer>
          ) : null}
        </Styled.ContentLayout>
      </Styled.AutoCompleteContainer>
    </Styled.SearchContainer>
  )
}
